import { Grid, Typography, Box } from "@mui/material";
import { formatFullAddress as _formatFullAddress } from "../../../utils/common";
import styles from "./index.module.css";
import { resolveInvoiceDocumentPayableAmount } from "../../../utils/invoicePaymentSummary";
import { getInvoiceDocumentIdentity } from "../../../utils/invoiceDeposits";

function formatDate(dateStr) {
  const date = new Date(dateStr);

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const fmtMoney = (value, currency = "CAD") => {
  const n = Number(value);

  const amt = Number.isFinite(n)
    ? n
    : parseFloat(String(value).replace(/[^0-9.-]/g, "")) || 0;

  return `${String(currency || "CAD").toUpperCase()} ${amt.toFixed(2)}`;
};

/**
 * Address helpers
 */
const normalizeAddress = (addr) => {
  if (!addr || addr === "1" || addr === 1) return null;

  if (typeof addr === "string") {
    const trimmed = addr.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object" ? parsed : trimmed;
    } catch {
      // Legacy invoices and Sales Orders may persist a perfectly usable
      // formatted address as plain text rather than JSON.
      return trimmed;
    }
  }

  return typeof addr === "object" ? addr : null;
};

function resolveBillingShipping(billing, shipping) {
  return {
    billingAddress: normalizeAddress(billing),
    shippingAddress: normalizeAddress(shipping),
  };
}

function normalizeAddressFields(a) {
  const addr = a || {};

  const line1 =
    addr.line1 ??
    addr.address1 ??
    addr.addressLine1 ??
    addr.streetAddress ??
    addr.street ??
    "";

  const line2 =
    addr.line2 ??
    addr.address2 ??
    addr.addressLine2 ??
    addr.suiteApt ??
    "";

  const city =
    addr.city ??
    addr.town ??
    "";

  const prov =
    addr.province ??
    addr.state ??
    addr.stateProvince ??
    "";

  const postal =
    addr.postal ??
    addr.zip ??
    addr.postalCode ??
    addr.postalZipCode ??
    "";

  const country = addr.country ?? "";

  return {
    name: addr.name ?? "",
    line1,
    line2,
    city,
    province: prov,
    postal,
    country,
  };
}

function buildMultilineAddress(addrObj) {
  const a = normalizeAddressFields(addrObj);

  const lines = [
    a.name || null,
    [a.line1, a.line2].filter(Boolean).join(", ") || null,
    [a.city, a.province].filter(Boolean).join(", ") || null,
    [a.postal, a.country].filter(Boolean).join(", ") || null,
  ].filter(Boolean);

  return lines.join("\n");
}

function safeFormatFullAddress(addr) {
  if (typeof addr === "string") {
    return addr.trim();
  }

  const built = buildMultilineAddress(addr);

  if (built && built.trim().length > 0) {
    return built;
  }

  try {
    const raw = _formatFullAddress?.(addr) ?? "";

    const cleaned = String(raw)
      .replace(/\bundefined\b|null/gi, "")
      .replace(/\s+,/g, ",")
      .trim();

    return cleaned || built || "";
  } catch {
    return built || "";
  }
}

/**
 * Consistent invoice metadata field.
 */
const MetaField = ({
  label,
  value,
  valueSx = {},
}) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        pr: 1.5,
      }}
    >
      <Typography
        className={styles.title}
        sx={{
          mb: "5px !important",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>

      <Typography
        className={styles.value}
        sx={{
          lineHeight: 1.45,
          overflowWrap: "anywhere",
          ...valueSx,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/**
 * Compact billing / shipping address block.
 */
const AddressBlock = ({
  label,
  name,
  address,
}) => {
  const formattedAddress = safeFormatFullAddress(address);
  const hasAddress = Boolean(formattedAddress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.35,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: "2px !important",
          lineHeight: 1.25,
        }}
      >
        {label}
      </Typography>

      {name && (
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.35,
            mb: 0,
          }}
        >
          {name}
        </Typography>
      )}

      <Typography
        variant="body1"
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.4,
          mb: 0,
          color: hasAddress ? "inherit" : "text.secondary",
        }}
      >
        {hasAddress ? formattedAddress : "N/A"}
      </Typography>
    </Box>
  );
};

/**
 * Generated invoice details / metadata.
 */
const InvoiceDetails = ({ invoiceDetails, finalOrderTotal }) => {
  // orderId is the project-facing OrderId used by /sales/orders/:orderId
  const projectId = invoiceDetails?.orderId;

  const invoiceDate = invoiceDetails?.invoiceDate
    ? formatDate(invoiceDetails.invoiceDate)
    : "";

  const customerPo = String(
    invoiceDetails?.customerPO || ""
  ).trim();

  const customerName = String(
    invoiceDetails?.customerName || ""
  ).trim();

  const documentIdentity =
    getInvoiceDocumentIdentity(invoiceDetails);

  const { billingAddress, shippingAddress } =
    resolveBillingShipping(
      invoiceDetails?.billingAddress,
      invoiceDetails?.shippingAddress
    );

  const pickLegacyName = (value) => {
    const text = String(value || "").trim();

    if (!text) return "";

    // Prevent raw numeric IDs from being printed as names.
    if (/^\d+$/.test(text)) return "";

    return text;
  };

  const billingName =
    String(invoiceDetails?.billingName || "").trim() ||
    pickLegacyName(invoiceDetails?.billingId);

  const shippingName =
    String(invoiceDetails?.shippingName || "").trim() ||
    pickLegacyName(invoiceDetails?.shippingId);

  const paid = [invoiceDetails?.paymentStatus, invoiceDetails?.invoiceStatus]
    .some((value) => ['paid', 'completed', 'succeeded', 'success']
      .includes(String(value || '').trim().toLowerCase()));
  const dueAmount = paid ? 0 : resolveInvoiceDocumentPayableAmount(
    invoiceDetails,
    finalOrderTotal
  );

  const amountDue = fmtMoney(
    dueAmount,
    invoiceDetails?.currency
  );

  const invoiceNumber =
    `${documentIdentity.numberPrefix} - ${invoiceDetails?.id}`;

  const billingAddressText = safeFormatFullAddress(billingAddress);
  const shippingAddressText = safeFormatFullAddress(shippingAddress);
  const termsText = invoiceDetails?.terms?.title || "C.O.D";

  return (
    <>
      <section className={styles.printMetaOnly}>
        <div className={styles.invoiceMetaGrid}>
          <section className={styles.printMetaSection}>
            <div className={styles.printMetaLabel}>Billing Address</div>
            {billingName ? <div className={styles.printAddressName}>{billingName}</div> : null}
            <div className={styles.printMetaValue}>
              {billingAddressText
                ? billingAddressText.split("\n").map((line, index) => (
                    <div key={"billing-" + index}>{line}</div>
                  ))
                : "N/A"}
            </div>
          </section>

          <section className={styles.printMetaSection}>
            <div className={styles.printMetaLabel}>Shipping Address</div>
            {shippingName ? <div className={styles.printAddressName}>{shippingName}</div> : null}
            <div className={styles.printMetaValue}>
              {shippingAddressText
                ? shippingAddressText.split("\n").map((line, index) => (
                    <div key={"shipping-" + index}>{line}</div>
                  ))
                : "N/A"}
            </div>
          </section>

          <section className={styles.printMetaSection}>
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>Project #</div>
              <div className={styles.printMetaValue}>{projectId || "—"}</div>
            </div>
            {customerName ? (
              <div className={styles.printMetaField}>
                <div className={styles.printMetaLabel}>Customer</div>
                <div className={styles.printMetaValue}>{customerName}</div>
              </div>
            ) : null}
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>Amount Due</div>
              <div className={`${styles.printMetaValue} ${styles.printAmountDue}`}>{amountDue}</div>
            </div>
          </section>

          <section className={styles.printMetaSection}>
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>{documentIdentity.numberLabel || "Invoice #"}</div>
              <div className={styles.printMetaValue}>{invoiceNumber}</div>
            </div>
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>Terms</div>
              <div className={styles.printMetaValue}>{termsText}</div>
            </div>
          </section>

          <section className={styles.printMetaSection}>
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>Invoice Date</div>
              <div className={styles.printMetaValue}>{invoiceDate || "—"}</div>
            </div>
            {customerPo ? (
              <div className={styles.printMetaField}>
                <div className={styles.printMetaLabel}>Customer PO</div>
                <div className={styles.printMetaValue}>{customerPo}</div>
              </div>
            ) : null}
            <div className={styles.printMetaField}>
              <div className={styles.printMetaLabel}>Currency</div>
              <div className={styles.printMetaValue}>
                {String(invoiceDetails?.currency || "CAD").toUpperCase()}
              </div>
            </div>
          </section>
        </div>
      </section>

      <Grid
        container
        className={`${styles.headerContainer} ${styles.screenMetaOnly}`}
        sx={{
          alignItems: "flex-start",
          width: "100%",
          marginLeft: 0,
          marginRight: 0
        }}
      >
      {/* Left: Billing & Shipping */}
      <Grid
        item
        xs={5}
        className={styles.addressContainer}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
            pr: 2,
          }}
        >
          <AddressBlock
            label="Billing Address"
            name={billingName}
            address={billingAddress}
          />

          <AddressBlock
            label="Shipping Address"
            name={shippingName}
            address={shippingAddress}
          />
        </Box>
      </Grid>

      {/* Right: Invoice Metadata */}
      <Grid
        item
        xs={7}
        className={styles.TitleContainer}
        sx={{
          pl: "20px !important",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "minmax(90px, 0.9fr) minmax(140px, 1.35fr) minmax(105px, 1fr)",
            columnGap: 3,
            rowGap: 2.5,
            width: "100%",
            alignItems: "start",
          }}
        >
          <MetaField
            label="Project #"
            value={projectId}
          />

          <MetaField
            label={paid ? "Balance Due (Paid)" : "Amount Due"}
            value={amountDue}
            valueSx={{
              fontSize: "17px !important",
            }}
          />

          <MetaField
            label={documentIdentity.numberLabel || "Invoice #"}
            value={invoiceNumber}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              customerPo
                ? "minmax(170px, 1.7fr) minmax(150px, 1.35fr) minmax(110px, 1fr) minmax(105px, 1fr) minmax(70px, 0.65fr)"
                : "minmax(185px, 1.8fr) minmax(165px, 1.45fr) minmax(120px, 1fr) minmax(75px, 0.7fr)",
            columnGap: 2.5,
            rowGap: 2.5,
            width: "100%",
            alignItems: "start",
            mt: 3,
          }}
        >
          <MetaField
            label="Terms"
            value={invoiceDetails?.terms?.title || "C.O.D"}
          />

          <MetaField
            label="Customer"
            value={customerName}
            valueSx={{
              fontWeight: 700,
            }}
          />

          <MetaField
            label="Invoice Date"
            value={invoiceDate}
          />

          {customerPo && (
            <MetaField
              label="Customer PO"
              value={customerPo}
            />
          )}

          <MetaField
            label="Currency"
            value={String(
              invoiceDetails?.currency || "CAD"
            ).toUpperCase()}
          />
        </Box>
      </Grid>
      </Grid>
    </>
  );
};

export default InvoiceDetails;